import type { EntityStatus } from '@ms/yammer-data-support';
import { getStatus } from '@ms/yammer-data-support';
import createCachedSelector from 're-reselect';

import type { DelegationTeam, User, YammerState } from '../../state/types';
import { getCurrentUser, getFulfilledUsers } from '../users/selectors';

type GetCurrentDelegationTeams = (state: YammerState) => Record<User['id'], DelegationTeam> | undefined;
/**
 * @deprecated PERFORMANCE This returns all fullfilled entities and will force unnecessary re-renders when any of them change.
 */
export const getCurrentDelegationTeams: GetCurrentDelegationTeams = (state: YammerState) =>
  state.delegationTeams.fulfilled;

type GetCurrentDelegationTeamsStatus = (state: YammerState) => EntityStatus;
export const getCurrentDelegationTeamsStatus: GetCurrentDelegationTeamsStatus = (state) =>
  getStatus(state.delegationTeams);

type GetOwnDelegationTeam = (state: YammerState, userId: User['id']) => DelegationTeam | undefined;
/**
 * @deprecated PERFORMANCE This returns an entity objec t and will force unnecessary re-renders when any of them change.
 * This should be replaced with a `getOwnDelegationTeamField` selector.
 */
export const getOwnDelegationTeam: GetOwnDelegationTeam = createCachedSelector(
  (_state: YammerState, userId: User['id']) => userId,
  getCurrentDelegationTeams,
  (userId, currentDelegationTeams) => {
    if (!currentDelegationTeams) {
      return undefined;
    }

    return currentDelegationTeams[userId];
  }
)((_state, userId) => userId);

type GetOtherDelegationTeams = (state: YammerState, userId: User['id']) => DelegationTeam[];
/**
 * @deprecated PERFORMANCE This returns a list of entities and will force unnecessary re-renders when any of them change.
 * If a component must get a list of entities it should select a memoized list of ids only, then pass each id to a child
 * component which will select the specific entity fields it requires and manage its own memoization to avoid re-renders.
 */
export const getOtherDelegationTeams: GetOtherDelegationTeams = createCachedSelector(
  (_state: YammerState, userId: User['id']) => userId,
  getCurrentDelegationTeams,
  (userId, currentDelegationTeams) => {
    if (!currentDelegationTeams) {
      return [];
    }

    const { [userId]: ownDelegationTeam, ...otherDelegationTeams } = currentDelegationTeams;

    return Object.values(otherDelegationTeams);
  }
)((_state, userId) => userId);

type GetDelegationTeamUsersForCurrentUser = (state: YammerState) => User[];
/**
 * @deprecated PERFORMANCE This returns a list of User entities and will force unnecessary re-renders when any of them change.
 * If a component must get a list of entities it should select a memoized list of ids only, then pass each id to a child
 * component which will select the specific entity fields it requires and manage its own memoization to avoid re-renders.
 */
export const getDelegationTeamUsersForCurrentUser: GetDelegationTeamUsersForCurrentUser = createCachedSelector(
  getCurrentDelegationTeams,
  getCurrentUser,
  getFulfilledUsers,
  (onBehalfOfSenders, currentUser, users) => {
    if (!onBehalfOfSenders) {
      return [currentUser];
    }

    return Object.values(onBehalfOfSenders).reduce((senders, user) => {
      const sender = users[user.ownerId];

      if (sender) {
        return [...senders, sender];
      }

      return senders;
    }, []);
  }
)((_state) => 'cache');

type GetDelegationTeamUsersForCurrentUserStoryline = (state: YammerState) => User[];
/**
 * @deprecated PERFORMANCE This returns a list of User entities and will force unnecessary re-renders when any of them change.
 * If a component must get a list of entities it should select a memoized list of ids only, then pass each id to a child
 * component which will select the specific entity fields it requires and manage its own memoization to avoid re-renders.
 */
export const getDelegationTeamUsersForCurrentUserStoryline: GetDelegationTeamUsersForCurrentUserStoryline =
  createCachedSelector(
    getCurrentDelegationTeams,
    getCurrentUser,
    getFulfilledUsers,
    (onBehalfOfSenders, currentUser, users) => {
      if (!onBehalfOfSenders) {
        return [currentUser];
      }

      return Object.values(onBehalfOfSenders).reduce((senders, user) => {
        const sender = users[user.ownerId];
        const senderHasPermission = user.viewerCanPostToStoryline || sender?.id === currentUser.id;

        if (sender && senderHasPermission) {
          return [...senders, sender];
        }

        return senders;
      }, []);
    }
  )((_state) => 'cache');

const emptyTeamArray: DelegationTeam[] = [];
type GetAllTeamsWithLeadersForCurrentUser = (state: YammerState) => DelegationTeam[];
/**
 * @deprecated PERFORMANCE This returns a list of entity objects and will force unnecessary re-renders when any of them change.
 * If a component must get a list of entities it should select a memoized list of ids only, then pass each id to a child
 * component which will select the specific entity fields it requires and manage its own memoization to avoid re-renders.
 */
const getAllTeamsWithLeadersForCurrentUser: GetAllTeamsWithLeadersForCurrentUser = createCachedSelector(
  getCurrentDelegationTeams,
  (delegationTeams) => {
    if (!delegationTeams) {
      return emptyTeamArray;
    }

    return Object.values(delegationTeams).filter((team) => !team.viewerIsOwner && team.isOwnerLeader);
  }
)((_state) => 'cache');

const emptyTeamLeaders: User[] = [];
type GetAllDelegationTeamLeadersForCurrentUser = (state: YammerState) => User[];
/**
 * @deprecated PERFORMANCE This returns a list of entity objects and will force unnecessary re-renders when any of them change.
 * If a component must get a list of entities it should select a memoized list of ids only, then pass each id to a child
 * component which will select the specific entity fields it requires and manage its own memoization to avoid re-renders.
 */
export const getAllDelegationTeamLeadersForCurrentUser: GetAllDelegationTeamLeadersForCurrentUser =
  createCachedSelector(getAllTeamsWithLeadersForCurrentUser, getFulfilledUsers, (teamsWithLeaders, users) => {
    if (teamsWithLeaders.length === 0) {
      return emptyTeamLeaders;
    }

    return teamsWithLeaders.reduce<User[]>((teams, team) => {
      const leader = users[team.ownerId];
      if (leader) {
        teams.push(leader);
      }

      return teams;
    }, []);
  })((_state) => 'cache');

type GetDelegationTeamWithHighestAudienceLeader = (state: YammerState) => DelegationTeam | null;
/**
 * @deprecated PERFORMANCE This returns a list of entity objects and will force unnecessary re-renders when any of them change.
 * If a component must get a list of entities it should select a memoized list of ids only, then pass each id to a child
 * component which will select the specific entity fields it requires and manage its own memoization to avoid re-renders.
 */
export const getDelegationTeamWithHighestAudienceLeader: GetDelegationTeamWithHighestAudienceLeader =
  createCachedSelector(getAllTeamsWithLeadersForCurrentUser, (teamsWithLeaders) => {
    if (teamsWithLeaders.length === 0) {
      return null;
    }

    return teamsWithLeaders.reduce(
      (highestAudienceLeader, teamLeader) =>
        teamLeader.ownerDirectAudience > highestAudienceLeader.ownerDirectAudience ? teamLeader : highestAudienceLeader,
      teamsWithLeaders[0]
    );
  })((_state) => 'cache');

type GetDelegationManagerUsersForCurrentUser = (state: YammerState) => User[];
/**
 * @deprecated PERFORMANCE This returns a list of entity objects and will force unnecessary re-renders when any of them change.
 * If a component must get a list of entities it should select a memoized list of ids only, then pass each id to a child
 * component which will select the specific entity fields it requires and manage its own memoization to avoid re-renders.
 */
export const getDelegationManagerUsersForCurrentUser: GetDelegationManagerUsersForCurrentUser = createCachedSelector(
  getCurrentDelegationTeams,
  getCurrentUser,
  getFulfilledUsers,
  (onBehalfOfSenders, currentUser, users) => {
    if (!onBehalfOfSenders) {
      return [currentUser];
    }

    return Object.values(onBehalfOfSenders).reduce((senders, user) => {
      const sender = users[user.ownerId];
      const senderHasPermission = user.viewerCanManageTeam || sender?.id === currentUser.id;

      if (sender && senderHasPermission) {
        return [...senders, sender];
      }

      return senders;
    }, []);
  }
)((_state) => 'cache');

type CanViewerManageTeam = (state: YammerState, leaderId: User['id']) => boolean;
export const canViewerManageTeam: CanViewerManageTeam = (state, leaderId) => {
  const delegationTeams = getCurrentDelegationTeams(state);
  if (!delegationTeams) {
    return false;
  }

  return !!delegationTeams[leaderId]?.viewerCanManageTeam;
};

type CanViewerPostToStoryline = (state: YammerState, leaderId: User['id'] | undefined) => boolean;
export const canViewerPostToStoryline: CanViewerPostToStoryline = (state, leaderId) => {
  if (!leaderId) {
    return false;
  }

  const delegationTeams = getCurrentDelegationTeams(state);
  if (!delegationTeams) {
    return false;
  }

  return !!delegationTeams[leaderId]?.viewerCanPostToStoryline;
};

type HasNetworkWideAudienceForLeader = (state: YammerState, leaderId: User['id'] | undefined) => boolean;
export const hasNetworkWideAudienceForLeader: HasNetworkWideAudienceForLeader = (state, leaderId) => {
  const delegationTeams = getCurrentDelegationTeams(state);
  if (!leaderId || !delegationTeams) {
    return false;
  }

  return !!delegationTeams[leaderId]?.hasNetworkWideAudience;
};

type CanViewerPostStorylineAnnouncementForLeader = (state: YammerState, leaderId: User['id'] | undefined) => boolean;
export const canViewerPostStorylineAnnouncementForLeader: CanViewerPostStorylineAnnouncementForLeader = (
  state,
  leaderId
) => {
  if (!leaderId) {
    return false;
  }

  const delegationTeams = getCurrentDelegationTeams(state);
  if (!delegationTeams) {
    return false;
  }

  return delegationTeams[leaderId]?.viewerCanPostAnnouncementToStoryline;
};

type CanPostOnBehalfOfUser = (state: YammerState, onBehalfOfUserId: User['id']) => boolean;
export const canPostOnBehalfOfUser: CanPostOnBehalfOfUser = (state, onBehalfOfUserId) => {
  const delegationTeamUsers = getDelegationTeamUsersForCurrentUserStoryline(state);

  return !!delegationTeamUsers.find((user) => user.id === onBehalfOfUserId);
};

type CanManageOnBehalfOfUser = (state: YammerState, onBehalfOfUserId: User['id'] | undefined) => boolean;
export const canManageOnBehalfOfUser: CanManageOnBehalfOfUser = (state, onBehalfOfUserId) => {
  if (!onBehalfOfUserId) {
    return false;
  }
  const delegationTeamUsers = getDelegationManagerUsersForCurrentUser(state);

  return !!delegationTeamUsers.find((user) => user.id === onBehalfOfUserId);
};
